import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TabsContent } from '@/components/ui/tabs';

const AdminSupportTicketsTab = ({
  supportTickets,
  expandedTicketId,
  setExpandedTicketId,
  getTicketStatusColor,
  ticketNotes,
  setTicketNotes,
  privateTicketNotes,
  setPrivateTicketNotes,
  handleUpdateTicket,
}) => {
  return (
    <TabsContent value="support">
      {supportTickets.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <p className="text-gray-400 text-lg">No support tickets yet.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="support-tickets-list">
          {supportTickets.map((ticket) => (
            <div key={ticket.id} className="glass-card p-6" data-testid={`support-ticket-${ticket.id}`}>
              <div className="space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => setExpandedTicketId((prev) => (prev === ticket.id ? null : ticket.id))}
                    className="flex-1 text-left"
                  >
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <h3 className="text-xl font-bold text-[#f0d9b5]">{ticket.subject}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTicketStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400 mb-1">Ticket</p>
                        <p className="font-semibold">{ticket.ticket_number}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-1">Category</p>
                        <p className="font-semibold">{ticket.category}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-1">Customer</p>
                        <p className="font-semibold">{ticket.name}</p>
                        <p className="text-gray-400">{ticket.email}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-1">Time</p>
                        <p className="font-semibold">{new Date(ticket.created_at).toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-1">Message</p>
                        <p className="text-gray-300 whitespace-pre-line line-clamp-2">{ticket.message}</p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpandedTicketId((prev) => (prev === ticket.id ? null : ticket.id))}
                    className="px-4 py-2 rounded-lg bg-[#d4a574]/10 text-[#d4a574] hover:bg-[#d4a574]/20 transition-colors flex items-center justify-center gap-2 shrink-0"
                  >
                    {expandedTicketId === ticket.id ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Collapse
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Expand
                      </>
                    )}
                  </button>
                </div>

                {expandedTicketId === ticket.id && (
                  <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 pt-4 border-t border-[#d4a574]/10">
                    <div className="space-y-4">
                      <div>
                        <p className="text-gray-400 mb-2 text-sm">Full Message</p>
                        <p className="text-gray-300 whitespace-pre-line leading-relaxed">{ticket.message}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 mb-2 text-sm">Response History</p>
                        {ticket.responses && ticket.responses.length > 0 ? (
                          <div className="space-y-3">
                            {ticket.responses.map((note, idx) => (
                              <div key={`${ticket.id}-note-${idx}`} className="rounded-lg border border-[#d4a574]/10 bg-[#0f0f0f] p-4">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                  <div>
                                    <p className="font-semibold text-[#f0d9b5]">
                                      {note.sender_name} {note.sender_role === 'admin' ? '(Team)' : '(User)'}
                                    </p>
                                    <p className="text-sm text-gray-500">{note.sender_email}</p>
                                  </div>
                                  <p className="text-sm text-gray-500">{new Date(note.created_at).toLocaleString('en-IN')}</p>
                                </div>
                                <p className="text-gray-300 whitespace-pre-line">{note.message}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500">No responses yet, please respond to this ticket.</p>
                        )}
                      </div>
                      <div>
                        <p className="text-gray-400 mb-2 text-sm">Private Admin Notes</p>
                        {ticket.private_admin_notes && ticket.private_admin_notes.length > 0 ? (
                          <div className="space-y-3">
                            {ticket.private_admin_notes.map((note, idx) => (
                              <div key={`${ticket.id}-private-note-${idx}`} className="rounded-lg border border-red-500/20 bg-red-500/5 p-4">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                  <div>
                                    <p className="font-semibold text-red-300">{note.admin_name}</p>
                                    <p className="text-sm text-gray-500">{note.admin_email}</p>
                                  </div>
                                  <p className="text-sm text-gray-500">{new Date(note.created_at).toLocaleString('en-IN')}</p>
                                </div>
                                <p className="text-gray-300 whitespace-pre-line">{note.note}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500">No private admin notes yet.</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Status</Label>
                        <select
                          value={ticket.status}
                          onChange={(e) => handleUpdateTicket(ticket.id, e.target.value)}
                          className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white focus:outline-none focus:border-[#d4a574]"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-gray-300 mb-2 block">Team Response</Label>
                        <Textarea
                          value={ticketNotes[ticket.id] || ''}
                          onChange={(e) => setTicketNotes((prev) => ({ ...prev, [ticket.id]: e.target.value }))}
                          className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                          rows={5}
                          placeholder="Write a team response..."
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300 mb-2 block">Private Admin Note</Label>
                        <Textarea
                          value={privateTicketNotes[ticket.id] || ''}
                          onChange={(e) => setPrivateTicketNotes((prev) => ({ ...prev, [ticket.id]: e.target.value }))}
                          className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-red-500/20 text-white"
                          rows={4}
                          placeholder="Visible only to admins..."
                        />
                      </div>
                      <button
                        onClick={() => handleUpdateTicket(ticket.id, ticket.status)}
                        className="btn-primary w-full"
                      >
                        Save Updates
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </TabsContent>
  );
};

export default AdminSupportTicketsTab;
